<!--
  OrangeHRM Calendar Plugin — Day Cell
  Renders the date number, holiday tag (at the right of date row),
  slight red tint for holidays, attendance ring (non-interactive),
  and event chips with "+X more" opening a scrollable dialog.
-->
<template>
  <div
    class="cal-cell"
    :class="{
      'is-other-month': !day.isCurrentMonth,
      'is-today': day.isToday,
      'is-holiday': holidays.length > 0,
      'is-non-working': isNonWorking,
      'is-blocked': isBlockedForEvents,
      'is-admin-clickable': canAddEventOnDay,
    }"
    :title="cellTitle"
    @click="handleCellClick"
  >
    <!-- Top row: Date number on left, Holiday tag or non-working tag on right -->
    <div class="cal-cell-top">
      <div class="cal-date-num">
        <span class="cal-date-num-inner">{{ day.date.getDate() }}</span>
      </div>
      <div
        v-if="holidays.length > 0"
        class="cal-holiday-tag is-clickable"
        @click.stop="$emit('holiday-click', holidays)"
      >
        {{ holidayDisplayName }}
      </div>
      <div
        v-else-if="isThirdSat"
        class="cal-nonwork-tag"
        :class="{'is-clickable': isAdmin}"
        @click.stop="onNonWorkTagClick('3rd Saturday Off')"
      >
        3rd Sat Off
      </div>
    </div>

    <!-- Event chips — max 2 visible + "+N more" overflow -->
    <div class="cal-event-list">
      <div
        v-for="event in visibleEvents"
        :key="event.id"
        class="cal-event-chip"
        :title="event.title"
        @click.stop="$emit('event-click', event)"
      >
        {{ event.title }}
      </div>
      <div
        v-if="overflowCount > 0"
        class="cal-overflow-chip"
        @click.stop="showMoreModal = true"
      >
        +{{ overflowCount }} more
      </div>
    </div>

    <!-- More Events Dialog using native OXD Dialog -->
    <oxd-dialog
      v-if="showMoreModal"
      class="orangehrm-dialog-popup"
      @update:show="showMoreModal = false"
    >
      <div class="orangehrm-modal-header">
        <oxd-text type="card-title">
          {{ formattedDateTitle }} &middot; More Events
        </oxd-text>
      </div>
      <oxd-divider />
      <div class="orangehrm-modal-content cal-more-list">
        <div
          v-for="event in overflowEvents"
          :key="event.id"
          class="cal-more-item"
          @click.stop="onSelectMoreEvent(event)"
        >
          <div class="cal-more-item-time">
            {{ formatEventTime(event.eventDatetime) }}
          </div>
          <div class="cal-more-item-details">
            <span class="cal-more-item-title">{{ event.title }}</span>
            <span v-if="event.location" class="cal-more-item-loc">
              {{ event.location }}
            </span>
          </div>
        </div>
      </div>
      <oxd-divider />
      <div class="orangehrm-modal-footer">
        <oxd-button
          display-type="ghost"
          label="Close"
          @click.stop="showMoreModal = false"
        />
      </div>
    </oxd-dialog>
  </div>
</template>

<script>
import {ref, computed} from 'vue';
import {OxdDialog, OxdButton, OxdText, OxdDivider} from '@ohrm/oxd';
import {isNonWorkingDay, isThirdSaturday} from '../utils/calendarUtils';

const MAX_VISIBLE = 2;

export default {
  name: 'DayCell',

  components: {
    'oxd-dialog': OxdDialog,
    'oxd-button': OxdButton,
    'oxd-text': OxdText,
    'oxd-divider': OxdDivider,
  },

  props: {
    day: {type: Object, required: true},
    events: {type: Array, default: () => []},
    holidays: {type: Array, default: () => []},
    workweek: {type: Object, default: null},
    isAdmin: {type: Boolean, default: false},
  },

  emits: ['day-click', 'event-click', 'holiday-click', 'add-holiday-date'],

  setup(props, {emit}) {
    const showMoreModal = ref(false);

    const visibleEvents = computed(() => props.events.slice(0, MAX_VISIBLE));
    const overflowEvents = computed(() => props.events.slice(MAX_VISIBLE));
    const overflowCount = computed(() =>
      Math.max(0, props.events.length - MAX_VISIBLE),
    );

    const isNonWorking = computed(() =>
      isNonWorkingDay(props.day.date, props.workweek),
    );

    const isThirdSat = computed(() =>
      isThirdSaturday(props.day.date),
    );

    const isBlockedForEvents = computed(() =>
      props.holidays.length > 0 || isNonWorking.value,
    );

    const canAddEventOnDay = computed(() =>
      props.isAdmin && !isBlockedForEvents.value,
    );

    const cellTitle = computed(() => {
      if (props.holidays.length > 0 || isThirdSat.value) return '';
      if (isNonWorking.value) {
        return (
          'Non-Working Day' +
          (props.isAdmin ? ' — Events cannot be scheduled' : '')
        );
      }
      return props.isAdmin ? 'Click to add event' : '';
    });

    const holidayDisplayName = computed(() => {
      if (!props.holidays.length) return '';
      const first = props.holidays[0];
      const suffix = first.length === 4 ? ' (Half Day)' : '';
      return `${first.name}${suffix}`;
    });

    const formattedDateTitle = computed(() => {
      const d = props.day?.date;
      if (!d) return 'Events';
      return d.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      });
    });

    function formatEventTime(datetimeStr) {
      if (!datetimeStr) return '';
      const dt = new Date(datetimeStr.replace(' ', 'T'));
      if (isNaN(dt.getTime())) {
        return datetimeStr.slice(11, 16) || datetimeStr;
      }
      return dt.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    }

    function onSelectMoreEvent(event) {
      showMoreModal.value = false;
      emit('event-click', event);
    }

    function onNonWorkTagClick(name) {
      if (props.isAdmin) {
        emit('add-holiday-date', {date: props.day.date, name});
      }
    }

    function handleCellClick() {
      if (canAddEventOnDay.value) {
        emit('day-click', props.day);
      }
    }

    return {
      showMoreModal,
      visibleEvents,
      overflowEvents,
      overflowCount,
      isNonWorking,
      isThirdSat,
      isBlockedForEvents,
      canAddEventOnDay,
      cellTitle,
      holidayDisplayName,
      formattedDateTitle,
      formatEventTime,
      onSelectMoreEvent,
      onNonWorkTagClick,
      handleCellClick,
    };
  },
};
</script>
<style lang="scss" scoped>
.orangehrm-modal-header {
  margin-bottom: 1.2rem;
  display: flex;
  justify-content: center;
}

.orangehrm-modal-footer {
  margin-top: 1.2rem;
  display: flex;
  justify-content: center;
}
</style>
